import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { HiOutlineCheckCircle, HiOutlineArrowRight } from "react-icons/hi2";
import { appConfig } from "../../../app-config";
import { APP_ROUTES } from "../../../data-access";
import "./payment-processing.scss";

const REDIRECT_SECONDS = 5;

const getRedirectConfig = (redirectParam: string | null) => {
  switch (redirectParam) {
    case "ai-job-researcher":
      return { path: APP_ROUTES.aiLeadGenerator, label: "AI Lead Generation" };
    case "team":
      return { path: APP_ROUTES.team, label: "Team" };
    default:
      return { path: APP_ROUTES.billing, label: "Billing" };
  }
};

export const PaymentProcessing = (): JSX.Element => {
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(window.location.search);
  const { path: redirectPath, label: redirectLabel } = getRedirectConfig(queryParams.get("redirect"));

  const goToDestination = useCallback(() => {
    navigate(redirectPath, { state: { allowedTransition: true } });
  }, [navigate, redirectPath]);

  useEffect(() => {
    if (!appConfig.enableGTAG || !window.gtag) return;

    // Google Ads conversion (existing — paid ads attribution).
    window.gtag("event", "conversion", {
      send_to: "AW-11007670916/_0ExCJjate4YEIT17oAp",
    });

    // GA4 `purchase` event for the landing -> signup -> purchase funnel.
    // We pass whatever Stripe drops in the success-redirect query string so
    // the event carries the transaction id, plan name, and amount.
    // Stripe Checkout default success_url contains
    //   ?session_id={CHECKOUT_SESSION_ID}
    // Our success_url also appends ?redirect=team|ai-job-researcher|... and
    // sometimes ?amount, ?plan when we control the redirect — capture what
    // exists, leave nulls otherwise so the event still fires.
    const transactionId =
      queryParams.get("session_id") || queryParams.get("transaction_id") || undefined;
    const amount = Number(queryParams.get("amount")) || undefined;
    const plan = queryParams.get("plan") || undefined;
    const currency = queryParams.get("currency") || "USD";

    window.gtag("event", "purchase", {
      transaction_id: transactionId,
      value: amount,
      currency,
      items: plan
        ? [{ item_id: plan, item_name: plan, price: amount, quantity: 1 }]
        : undefined,
    });
  }, [queryParams]);

  useEffect(() => {
    if (countdown <= 0) {
      goToDestination();
      return;
    }

    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, goToDestination]);

  const progress = ((REDIRECT_SECONDS - countdown) / REDIRECT_SECONDS) * 100;

  return (
    <div className="payment-processing">
      <div className="payment-processing__card">
        <div className="payment-processing__icon">
          <HiOutlineCheckCircle size={56} />
        </div>

        <h1 className="payment-processing__title">Payment Successful</h1>
        <p className="payment-processing__subtitle">
          Your subscription is now active. We're setting everything up for you.
        </p>

        <div className="payment-processing__progress">
          <div
            className="payment-processing__progress-fill"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="payment-processing__countdown">
          Redirecting in {countdown}s...
        </p>

        <Link
          to={redirectPath}
          state={{ allowedTransition: true }}
          className="payment-processing__link"
        >
          Go to {redirectLabel}
          <HiOutlineArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
};
